import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { isValidEmail } from "@faire/retailer-visitor-shared/lib/validation";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { useSettingSignUpSplit } from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { useFinishedSignupOnboarding } from "@faire/retailer-visitor-shared/views/SignUp/useFinishedSignupOnboarding";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import type { RetailerRoute } from "@faire/web/common/routes/RetailerRouteMatcher";
import { RouteMatcher } from "@faire/web/common/routes/RouteMatcher";
import { useQuery_escape_hatch as useEmailAvailabilityQuery } from "@faire/web-api--source/endpoints/www/api/user/availability/post-hooks";
import { ICheckEmailAddressAndBusinessAvailabilityRequest as EmailAvailabilityRequest } from "@faire/web-api--source/indigofair/data/ICheckEmailAddressAndBusinessAvailabilityRequest";
import homepage from "@faire/web-api--source/routes/www/root";
import { route as personalInfoRoute } from "@faire/web-api--source/routes/www/welcome/r/personal";
import qs from "query-string";
import React, { useEffect } from "react";
import { Redirect, useLocation } from "react-router-dom";

export const RedirectIfInvalid: React.FC = () => {
  const isSignUpSplitEnabled = useSettingSignUpSplit();
  const location = useLocation();
  const { hasFinishedSignupOnboarding } = useFinishedSignupOnboarding();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const {
    queryParams,
    isRetailerSubUser,
    isFromWidget,
    isFaireDirectRequireEmailAddress,
  } = useSignUpQueryParams();
  const { redirectUrl, shouldRedirect } = useCheckEmail(queryParams.email);
  const shouldRedirectToHome =
    hasFinishedSignupOnboarding && isLoggedInRetailer;

  // faireDirectWidget, faireDirectPDPWithoutEmailAddress sign up flow and subuser don't have email in the query params
  const mustRedirect =
    !isFaireDirectRequireEmailAddress &&
    !isFromWidget &&
    !isRetailerSubUser &&
    shouldRedirect &&
    routeMatcher.match(location.pathname);

  useEffect(() => {
    if ((mustRedirect || shouldRedirectToHome) && isSignUpSplitEnabled) {
      getWindowOrThrow().scrollTo?.(0, 0);
      // Hard reload when leaving signup-app
      getLocationOrThrow().href = shouldRedirectToHome ? home() : redirectUrl;
    }
  }, [mustRedirect, shouldRedirectToHome, isSignUpSplitEnabled, redirectUrl]);

  if (shouldRedirectToHome && !isSignUpSplitEnabled) {
    return <Redirect to={home()} />;
  }

  return mustRedirect && !isSignUpSplitEnabled ? (
    <Redirect to={redirectUrl} />
  ) : null;
};

const routeMatcher = new RouteMatcher<RetailerRoute>([personalInfoRoute]);

const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Sometimes, a user might try to signup with an email that is already in use.
 * This hook veifies if the email is available or not, returns a url for
 * redirection and whether or not to redirect.
 */
const useCheckEmail = (email?: string) => {
  const { isPending, data, isFetching } = useEmailAvailabilityQuery(
    EmailAvailabilityRequest.build({ email_address: email }),
    { staleTime: TEN_MINUTES, gcTime: TEN_MINUTES, enabled: !!email }
  );
  const { queryParams } = useSignUpQueryParams();

  const baseUrl = queryParams.source || homepage();
  const redirectUrl = `${baseUrl}?${qs.stringify(queryParams)}`;

  return {
    shouldRedirect:
      !isValidEmail(email) ||
      (!isPending && !isFetching && !data?.email_address_available),
    redirectUrl,
  };
};
